/**
 * Game Rating Utility
 * 
 * Rates how "good" a matchmaking-selected game was based on 4 criteria:
 * 1. Skill Evenness (ELO difference)
 * 2. Player Categorization Closeness
 * 3. Player Interactions (variety)
 * 4. Game Type Balance
 * 
 * Each criterion is scored 1-5, with 5 being the best.
 */

import { detectGameType } from './gameTypeUtils';
import { getCategoryGap, areCategoriesCompatible } from './playerCategorizationUtils';

const DEFAULT_ELO = 1200;

function getLevelsElo(player) {
  return player?.levels_elo_rating ?? DEFAULT_ELO;
}

function getMixedElo(player) {
  return player?.mixed_elo_rating ?? DEFAULT_ELO;
}

function normalizeGender(gender) {
  return (gender || '').toLowerCase();
}

/**
 * Balanced check for men's/women's doubles:
 * All four players' level Elos must be within 100.
 */
export function isBalancedDoubles(team1Players = [], team2Players = []) {
  const allPlayers = [...(team1Players || []), ...(team2Players || [])];
  if (allPlayers.length !== 4) return false;
  const ratings = allPlayers.map(getLevelsElo);
  const maxElo = Math.max(...ratings);
  const minElo = Math.min(...ratings);
  return maxElo - minElo <= 100;
}

/**
 * Balanced check for mixed doubles:
 * Male-vs-male within ±50 and female-vs-female within ±50 across teams.
 */
export function isBalancedMixed(team1Players = [], team2Players = []) {
  const findByGender = (team, gender) =>
    (team || []).find(p => normalizeGender(p?.gender) === gender);

  const team1Male = findByGender(team1Players, 'male');
  const team1Female = findByGender(team1Players, 'female');
  const team2Male = findByGender(team2Players, 'male');
  const team2Female = findByGender(team2Players, 'female');

  if (!team1Male || !team1Female || !team2Male || !team2Female) return false;

  const maleDiff = Math.abs(getMixedElo(team1Male) - getMixedElo(team2Male));
  const femaleDiff = Math.abs(getMixedElo(team1Female) - getMixedElo(team2Female));

  return maleDiff <= 50 && femaleDiff <= 50;
}

/**
 * Scoreline nudge banding for balanced games.
 * Used only by the legacy Elo policy (games before ELO_POLICY_V2_EFFECTIVE_AT).
 */
export function getScorelineNudge(scoreDiff = 0) {
  if (scoreDiff >= 11) return 12;
  if (scoreDiff >= 6) return 8;
  if (scoreDiff >= 3) return 4;
  if (scoreDiff >= 1) return 2;
  return 0;
}

/**
 * Winner result score from the scoreline (v2 policy).
 * Deuce means the game reached 20–20 (margin 2 and the higher score is at least 22).
 *
 * @returns {{ winnerScore: number, allowLoserGain: boolean, isDeuce: boolean }}
 */
export function getCompetitiveResultScore(team1Score, team2Score) {
  const s1 = Number(team1Score) || 0;
  const s2 = Number(team2Score) || 0;
  const margin = Math.abs(s1 - s2);
  const hi = Math.max(s1, s2);
  const isDeuce = margin === 2 && hi >= 22;

  if (isDeuce) {
    return { winnerScore: 0.55, allowLoserGain: true, isDeuce: true };
  }
  if (margin <= 2) {
    return { winnerScore: 0.7, allowLoserGain: false, isDeuce: false };
  }
  if (margin <= 6) {
    return { winnerScore: 0.9, allowLoserGain: false, isDeuce: false };
  }
  return { winnerScore: 1, allowLoserGain: false, isDeuce: false };
}

/**
 * A win never reduces a rating. A loss may increase a rating only on a deuce.
 * An even deuce that rounded to 0 still pays the winner +1. A favourite whose
 * deuce win rounded negative stays at 0.
 */
export function clampCompetitiveEloDelta(delta, { won, allowLoserGain }) {
  const n = Number(delta) || 0;
  if (won && n < 0) return 0;
  if (won && n === 0 && allowLoserGain) return 1;
  if (!won && n > 0 && !allowLoserGain) return 0;
  return n;
}

/**
 * Rate skill evenness based on team average ELO difference
 * 
 * @param {number} team1AvgElo - Average ELO for team 1
 * @param {number} team2AvgElo - Average ELO for team 2
 * @returns {number} Rating 1-5 (5 = best balance)
 */
export function rateSkillEvenness(team1AvgElo, team2AvgElo) {
  if (!team1AvgElo || !team2AvgElo) return 3; // Default if missing data
  
  const eloDiff = Math.abs(team1AvgElo - team2AvgElo);
  
  // Updated tiers based on user specifications:
  // 0-50: Excellent balance
  // 51-100: Good balance
  // 101-150: Moderate imbalance
  // 151-200: Poor balance
  // 200+: Very poor balance
  
  if (eloDiff <= 50) return 5;
  if (eloDiff <= 100) return 4;
  if (eloDiff <= 150) return 3;
  if (eloDiff <= 200) return 2;
  return 1;
}

/**
 * Rate player categorization closeness
 * Evaluates how close player categories are within and across teams
 * 
 * Key principles:
 * - Team balance matters most (AA vs BB is bad, AA vs AA is good)
 * - Within-team gaps matter (AB is better than AC)
 * - Same structure teams are best (AA vs AA, AB vs AB)
 * 
 * @param {Array} team1Players - Array of player objects with category property
 * @param {Array} team2Players - Array of player objects with category property
 * @returns {number} Rating 1-5 (5 = best categorization match)
 */
export function ratePlayerCategorization(team1Players, team2Players) {
  if (!team1Players || !team2Players || team1Players.length !== 2 || team2Players.length !== 2) {
    return 3; // Default if missing data
  }
  
  // Extract categories
  const team1Categories = team1Players.map(p => p.category).filter(Boolean);
  const team2Categories = team2Players.map(p => p.category).filter(Boolean);
  
  if (team1Categories.length !== 2 || team2Categories.length !== 2) {
    return 3; // Missing category data
  }
  
  // Sort categories within each team for consistent comparison
  const team1Sorted = [...team1Categories].sort();
  const team2Sorted = [...team2Categories].sort();
  
  // Calculate gaps within each team
  const team1Gap = getCategoryGap(team1Sorted[0], team1Sorted[1]);
  const team2Gap = getCategoryGap(team2Sorted[0], team2Sorted[1]);
  const maxWithinTeamGap = Math.max(team1Gap, team2Gap);
  
  // Check if teams have the same structure (same categories, order doesn't matter)
  const team1Str = team1Sorted.join('');
  const team2Str = team2Sorted.join('');
  const sameStructure = team1Str === team2Str;
  
  // Calculate team average category strength (A=0, B=1, C=2, D=3)
  const categoryToIndex = (cat) => {
    const idx = ['A', 'B', 'C', 'D'].indexOf(cat);
    return idx === -1 ? 2 : idx; // Default to C if unknown
  };
  
  const team1Avg = (categoryToIndex(team1Sorted[0]) + categoryToIndex(team1Sorted[1])) / 2;
  const team2Avg = (categoryToIndex(team2Sorted[0]) + categoryToIndex(team2Sorted[1])) / 2;
  const teamStrengthDiff = Math.abs(team1Avg - team2Avg);
  
  // Calculate max gap across all player pairs
  const allGaps = [];
  team1Categories.forEach(cat1 => {
    team2Categories.forEach(cat2 => {
      const gap = getCategoryGap(cat1, cat2);
      if (gap !== Infinity) {
        allGaps.push(gap);
      }
    });
  });
  const maxCrossTeamGap = Math.max(...allGaps);
  
  // Rating logic based on user's specifications:
  
  // Check if teams have different gap patterns (different structures)
  // Examples: AC vs BB (gap 2 vs gap 0), AA vs BC (gap 0 vs gap 1)
  const differentStructures = team1Gap !== team2Gap;
  
  // 5: Perfect match - same structure with gap ≤ 1
  // Examples: AA vs AA, AB vs AB, BB vs BB, CC vs CC, CD vs CD, DD vs DD
  if (sameStructure && maxWithinTeamGap <= 1) {
    return 5;
  }
  
  // 3: Acceptable - same structure with 2 gap
  // Examples: AC vs AC, AD vs AD, BD vs BD
  if (sameStructure && maxWithinTeamGap === 2) {
    return 3;
  }
  
  // 2: Poor - 3 gap scenarios (check this BEFORE different structures)
  // Examples: AC vs AD (gap 2 vs gap 3), BC vs BD (gap 1 vs gap 2, creates 3 gap)
  // Even if structures are different, if 3 gap is involved, it's a 2
  if (maxWithinTeamGap === 3 || maxCrossTeamGap === 3) {
    return 2;
  }
  
  // Now handle "one category shift" scenarios - check BEFORE general different structures:
  
  // 4: Almost perfect - one category shift, all players within 1 gap
  // Examples: AA vs AB, BB vs BC, CC vs CD
  // Condition: different structure but max gap ≤ 1 (close categories)
  if (!sameStructure && maxWithinTeamGap <= 1 && maxCrossTeamGap <= 1) {
    return 4;
  }
  
  // 3: Acceptable - one category shift with 2 gap
  // Examples: AB vs AC
  // Condition: different structure but creates max 2 gap
  if (!sameStructure && maxCrossTeamGap === 2) {
    return 3;
  }
  
  // 1: Very poor - different team structures (unbalanced)
  // Examples: AC vs BB, AA vs BC (with large gap), AD vs BB, AB vs CC, etc.
  // These are fundamentally unbalanced because teams have different gap patterns
  // and create large disparities
  if (differentStructures) {
    return 1;
  }
  
  // Default fallback
  return 2;
}

/**
 * Rate player interactions (variety)
 * Lower score if players have interacted frequently (less variety)
 * 
 * @param {Array} team1Players - Array of player objects with id
 * @param {Array} team2Players - Array of player objects with id
 * @param {Object} playerInteractions - Map of interaction counts: { "player1-player2": count }
 * @param {Object} gamesPlayedThisSession - Map of games played: { playerId: count }
 * @returns {number} Rating 1-5 (5 = best variety, low interactions)
 */
export function ratePlayerInteractions(team1Players, team2Players, playerInteractions = {}, gamesPlayedThisSession = {}) {
  if (!team1Players || !team2Players || team1Players.length !== 2 || team2Players.length !== 2) {
    return 3; // Default if missing data
  }
  
  const allPlayers = [...team1Players, ...team2Players];
  const playerIds = allPlayers.map(p => p.id || p);
  
  // Calculate interaction percentages for all pairs
  const interactionPercentages = [];
  
  for (let i = 0; i < playerIds.length; i++) {
    for (let j = i + 1; j < playerIds.length; j++) {
      const player1Id = playerIds[i];
      const player2Id = playerIds[j];
      // Handle both dash and underscore formats
      const keyDash = [player1Id, player2Id].sort().join('-');
      const keyUnderscore = [player1Id, player2Id].sort().join('_');
      
      const interactionCount = playerInteractions[keyDash] || 
                               playerInteractions[keyUnderscore] || 
                               0;
      const player1Games = gamesPlayedThisSession[player1Id] || 0;
      const player2Games = gamesPlayedThisSession[player2Id] || 0;
      const minGames = Math.min(player1Games, player2Games);
      
      if (minGames > 0) {
        const percentage = (interactionCount / minGames) * 100;
        interactionPercentages.push(percentage);
      } else {
        // No games played yet, assume good variety
        interactionPercentages.push(0);
      }
    }
  }
  
  if (interactionPercentages.length === 0) {
    return 5; // No interaction data, assume good variety
  }
  
  // Use maximum percentage (worst case) for rating
  const maxPercentage = Math.max(...interactionPercentages);
  
  // Rating tiers based on interaction percentage:
  // 0-10%: Excellent variety (5)
  // 11-20%: Good variety (4)
  // 21-30%: Moderate variety (3)
  // 31-40%: Poor variety (2)
  // 40%+: Very poor variety (1)
  
  if (maxPercentage <= 10) return 5;
  if (maxPercentage <= 20) return 4;
  if (maxPercentage <= 30) return 3;
  if (maxPercentage <= 40) return 2;
  return 1;
}

/**
 * Rate game type balance
 * 
 * @param {Array} team1Players - Array of player objects
 * @param {Array} team2Players - Array of player objects
 * @returns {number} Rating 1-5 (5 = best balance)
 */
export function rateGameTypeBalance(team1Players, team2Players) {
  if (!team1Players || !team2Players) return 3;
  
  const gameType = detectGameType(team1Players, team2Players);
  
  // Check if teams are balanced
  const team1Males = team1Players.filter(p => p.gender?.toLowerCase() === 'male').length;
  const team2Males = team2Players.filter(p => p.gender?.toLowerCase() === 'male').length;
  
  const team1IsMixed = team1Males === 1;
  const team2IsMixed = team2Males === 1;
  const team1IsAllMale = team1Males === 2;
  const team2IsAllMale = team2Males === 2;
  const team1IsAllFemale = team1Males === 0;
  const team2IsAllFemale = team2Males === 0;
  
  // Balanced: same game type (mixed vs mixed, mens vs mens, womens vs womens)
  if ((team1IsMixed && team2IsMixed) || 
      (team1IsAllMale && team2IsAllMale) || 
      (team1IsAllFemale && team2IsAllFemale)) {
    return 5;
  }
  
  // Mixed vs mens/womens: acceptable but not ideal
  if ((team1IsMixed && (team2IsAllMale || team2IsAllFemale)) ||
      (team2IsMixed && (team1IsAllMale || team1IsAllFemale))) {
    return 3;
  }
  
  // Unbalanced: all-male vs all-female (should be rare)
  if ((team1IsAllMale && team2IsAllFemale) || (team1IsAllFemale && team2IsAllMale)) {
    return 1;
  }
  
  // Default for other cases
  return 3;
}

/**
 * Calculate overall game rating
 * 
 * @param {Object} game - Game object with team data
 * @param {Object} options - Additional data needed for rating
 * @param {Object} options.playerInteractions - Interaction counts
 * @param {Object} options.gamesPlayedThisSession - Games played counts
 * @returns {Object} Rating object with individual scores and total
 */
export function calculateGameRating(game, options = {}) {
  const {
    playerInteractions = {},
    gamesPlayedThisSession = {},
  } = options;
  
  // Extract team data (handle both simulation and real session formats)
  const team1Players = game.team_1_players || game.team1Players || [];
  const team2Players = game.team_2_players || game.team2Players || [];
  
  // Get average ELOs (handle both formats)
  let team1AvgElo = game.team_1_avg_starting_elo || game.team1AvgElo;
  let team2AvgElo = game.team_2_avg_starting_elo || game.team2AvgElo;
  
  // Calculate from individual players if not available
  // Determine if this is a mixed game to use correct ELO type
  const isMixedGame = game.pair_type === 'mixed' || 
                      game.isMixed || 
                      (game.game_type && game.game_type.toLowerCase().includes('mixed'));
  
  if (!team1AvgElo && team1Players.length === 2) {
    const getElo = (player) => {
      if (player.starting_elo) return player.starting_elo;
      if (isMixedGame) return player.mixed_elo_rating || 1200;
      return player.levels_elo_rating || 1200;
    };
    const p1Elo = getElo(team1Players[0]);
    const p2Elo = getElo(team1Players[1]);
    team1AvgElo = (p1Elo + p2Elo) / 2;
  }
  
  if (!team2AvgElo && team2Players.length === 2) {
    const getElo = (player) => {
      if (player.starting_elo) return player.starting_elo;
      if (isMixedGame) return player.mixed_elo_rating || 1200;
      return player.levels_elo_rating || 1200;
    };
    const p1Elo = getElo(team2Players[0]);
    const p2Elo = getElo(team2Players[1]);
    team2AvgElo = (p1Elo + p2Elo) / 2;
  }
  
  // Calculate individual ratings
  const skillEvenness = rateSkillEvenness(team1AvgElo, team2AvgElo);
  const categorization = ratePlayerCategorization(team1Players, team2Players);
  const interactions = ratePlayerInteractions(team1Players, team2Players, playerInteractions, gamesPlayedThisSession);
  const gameTypeBalance = rateGameTypeBalance(team1Players, team2Players);
  
  const total = skillEvenness + categorization + interactions + gameTypeBalance;
  
  return {
    skillEvenness,
    categorization,
    interactions,
    gameTypeBalance,
    total,
    // Format as string for display (e.g., "5445", "18")
    ratingsString: `${skillEvenness}${categorization}${interactions}${gameTypeBalance}`,
  };
}

export default {
  rateSkillEvenness,
  ratePlayerCategorization,
  ratePlayerInteractions,
  rateGameTypeBalance,
  calculateGameRating,
};

